import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const DEFAULT_JSON = '{\n  "employeeId": null,\n  "payrollPeriodId": null\n}';

export interface WorkflowTestDataLoaderDialogProps {
  open: boolean;
  onClose: () => void;
  onRunTest: (triggerData: Record<string, unknown>) => Promise<void>;
}

export function WorkflowTestDataLoaderDialog({
  open,
  onClose,
  onRunTest,
}: WorkflowTestDataLoaderDialogProps) {
  const [json, setJson] = useState(DEFAULT_JSON);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const handleRun = useCallback(async () => {
    setError(null);
    const trimmed = json.trim() || '{}';
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(trimmed) as Record<string, unknown>;
      if (data !== null && typeof data !== 'object') {
        throw new Error('Trigger data must be a JSON object');
      }
      if (data === null) data = {};
    } catch (e) {
      setError(e instanceof SyntaxError ? e.message : 'Invalid JSON');
      return;
    }
    setIsRunning(true);
    try {
      await onRunTest(data ?? {});
      onClose();
    } finally {
      setIsRunning(false);
    }
  }, [json, onRunTest, onClose]);

  const handleClose = useCallback(() => {
    if (!isRunning) {
      setError(null);
      onClose();
    }
  }, [isRunning, onClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Test workflow</DialogTitle>
          <DialogDescription>
            Prepare trigger data to send into the workflow. Use a JSON object (e.g. employeeId,
            payrollPeriodId). Leave empty or {'{}'} to run with no data.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="trigger-data-json">Trigger data (JSON)</Label>
          <Textarea
            id="trigger-data-json"
            value={json}
            onChange={(e) => setJson(e.target.value)}
            placeholder='{ "key": "value" }'
            className="min-h-[140px] font-mono text-sm"
            disabled={isRunning}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isRunning}>
            Cancel
          </Button>
          <Button onClick={handleRun} disabled={isRunning}>
            {isRunning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running…
              </>
            ) : (
              'Run test'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
